import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Package, FileText, HelpCircle, Settings, LogOut, ExternalLink, Boxes, History, X } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { auditLog as auditLogApi } from '../lib/api'
import { getMsalInstance } from '../lib/msalConfig'
import { TopBar } from '../components/layout/TopBar'
import { Card } from '../components/ui/Card'
import { Modal } from '../components/ui/Modal'
import { LoadingSpinner } from '../components/ui/EmptyState'

const AKTION_CONFIG = {
  'erstellt':   { color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'bearbeitet': { color: 'bg-blue-100 text-blue-700',       dot: 'bg-blue-500' },
  'gelöscht':   { color: 'bg-red-100 text-red-700',         dot: 'bg-red-500' },
}

const MODUL_COLOR: Record<string, string> = {
  'Kunden':  'text-blue-600',
  'Inventar':'text-indigo-600',
  'Besuche': 'text-amber-600',
  'Defekte': 'text-red-600',
  'Service': 'text-purple-600',
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'Gerade eben'
  if (min < 60) return `vor ${min} Min.`
  const h = Math.floor(min / 60)
  if (h < 24) return `vor ${h} Std.`
  const d = Math.floor(h / 24)
  if (d < 7) return `vor ${d} Tag${d > 1 ? 'en' : ''}`
  return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

type AuditEntry = { id: string; benutzer_name: string; aktion: string; modul: string; bezeichnung: string; erstellt_am: string }

function groupByDay(entries: AuditEntry[]) {
  const groups: Record<string, AuditEntry[]> = {}
  for (const e of entries) {
    const day = new Date(e.erstellt_am).toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    if (!groups[day]) groups[day] = []
    groups[day].push(e)
  }
  return Object.entries(groups)
}

const MODULE_ITEMS = [
  { label: 'Inventar', icon: Boxes, to: '/inventar', color: 'text-blue-600 bg-blue-50', desc: 'Geräte pro Kunde verwalten' },
  { label: 'Defekte', icon: AlertTriangle, to: '/defekte', color: 'text-red-600 bg-red-50', desc: 'Defekte erfassen und verwalten' },
  { label: 'Ersatzteile', icon: Package, to: '/ersatzteile', color: 'text-amber-600 bg-amber-50', desc: 'Lagerbestand und Bestellungen' },
  { label: 'Offerten', icon: FileText, to: '/offerten', color: 'text-purple-600 bg-purple-50', desc: 'Angebote erstellen und senden' },
  { label: 'FAQ & Selbsthilfe', icon: HelpCircle, to: '/faq', color: 'text-emerald-600 bg-emerald-50', desc: 'Häufige Fragen und Lösungen' },
]

export function MehrPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const isAdmin = user?.rolle === 'admin'
  const [historyOpen, setHistoryOpen] = useState(false)
  const [modulFilter, setModulFilter] = useState<string>('')

  const { data: logData, isLoading: logLoading } = useQuery({
    queryKey: ['audit-log', modulFilter],
    queryFn: () => auditLogApi.list({ modul: modulFilter || undefined, limit: 150 }),
    enabled: historyOpen,
    staleTime: 30000,
  })

  const MODULE = ['', 'Kunden', 'Inventar', 'Besuche', 'Defekte', 'Service']

  return (
    <>
      <TopBar title="Mehr" />
      <div className="px-4 py-3 space-y-6 pb-8">

        {/* User Card */}
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-lg">
              {user?.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full inline-block mt-1 capitalize">{user?.rolle}</span>
          </div>
          <button
            onClick={() => setHistoryOpen(true)}
            className="w-10 h-10 rounded-2xl bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 transition-colors"
            title="Änderungshistorie"
          >
            <History size={18} />
          </button>
        </Card>

        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">Module</h3>
          <div className="space-y-2">
            {MODULE_ITEMS.map((item) => (
              <button
                key={item.to}
                onClick={() => navigate(item.to)}
                className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:border-blue-200 hover:shadow-md transition-all active:scale-[0.99]"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color}`}>
                  <item.icon size={20} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
                <ExternalLink size={16} className="text-gray-400" />
              </button>
            ))}
          </div>
        </div>

        {isAdmin && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">System</h3>
            <button
              onClick={() => navigate('/einstellungen')}
              className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:border-blue-200 hover:shadow-md transition-all active:scale-[0.99]"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-600 bg-gray-100">
                <Settings size={20} />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900">Einstellungen</p>
                <p className="text-xs text-gray-500">Benutzer und Konfiguration</p>
              </div>
              <ExternalLink size={16} className="text-gray-400" />
            </button>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={async () => {
            try {
              const msal = await getMsalInstance()
              await msal.clearCache()
            } catch { /* ignore */ }
            logout()
            navigate('/login')
          }}
          className="w-full flex items-center justify-center gap-2 py-3 text-red-600 font-medium rounded-2xl border border-red-200 hover:bg-red-50 transition-colors"
        >
          <LogOut size={18} />
          Abmelden
        </button>

        <p className="text-center text-xs text-gray-400">Vaporex Field Portal v1.0.0</p>
      </div>

      {/* ── Änderungshistorie Modal ─────────────────────────── */}
      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Änderungshistorie"
        size="lg"
      >
        {/* Modul-Filter */}
        <div className="flex gap-1.5 flex-wrap mb-4 -mt-1">
          {MODULE.map((m) => (
            <button
              key={m}
              onClick={() => setModulFilter(m)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                modulFilter === m
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {m || 'Alle'}
            </button>
          ))}
        </div>

        {logLoading ? (
          <LoadingSpinner />
        ) : !logData || logData.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">Noch keine Änderungen erfasst</p>
        ) : (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            {groupByDay(logData).map(([day, entries]) => (
              <div key={day}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 sticky top-0 bg-white py-1">
                  {day}
                </p>
                <div className="space-y-2">
                  {entries.map((entry) => {
                    const ak = AKTION_CONFIG[entry.aktion as keyof typeof AKTION_CONFIG] ?? AKTION_CONFIG['bearbeitet']
                    const mc = MODUL_COLOR[entry.modul] ?? 'text-gray-600'
                    return (
                      <div key={entry.id} className="flex items-start gap-3">
                        {/* Timeline dot */}
                        <div className="mt-1.5 flex flex-col items-center">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${ak.dot}`} />
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0 bg-gray-50 rounded-xl px-3 py-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-sm font-semibold text-gray-900">{entry.benutzer_name}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ak.color}`}>
                                  {entry.aktion}
                                </span>
                                <span className={`text-xs font-medium ${mc}`}>{entry.modul}</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.bezeichnung}</p>
                            </div>
                            <span className="text-xs text-gray-400 shrink-0 mt-0.5">
                              {formatRelative(entry.erstellt_am)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  )
}
